import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import DramaCard from "@/components/feature/DramaCard";
import ShareButton from "@/pages/detail/components/ShareButton";
import { getDramaById, getRelatedDramas, formatViews } from "@/utils/drama";
import { topics } from "@/mocks/dramas";
import { useJsonLd } from "@/hooks/useJsonLd";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const drama = id ? getDramaById(id) : undefined;

  useEffect(() => {
    document.title = drama
      ? `《${drama.title}》详情介绍 - 国产精品视频久久`
      : "影视资源不存在 - 国产精品视频久久";
    const meta = document.querySelector('meta[name="description"]');
    if (meta && drama) {
      meta.setAttribute(
        "description",
        `${drama.summary} 类型：${drama.genres.join("、")}，${drama.year} 年大陆${drama.category === "movie" ? "电影" : "电视剧"}，导演 ${drama.director}。`,
      );
    }
    return () => {
      document.title = "国产精品久久 - 高清国产影视大片汇聚平台";
    };
  }, [drama]);

  useJsonLd(
    "detail-movie-jsonld",
    drama
      ? {
          "@context": "https://schema.org",
          "@type": drama.category === "movie" ? "Movie" : "TVSeries",
          name: drama.title,
          description: drama.synopsis,
          datePublished: String(drama.year),
          genre: drama.genres,
          director: { "@type": "Person", name: drama.director },
          actor: drama.cast.map((name) => ({ "@type": "Person", name })),
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: drama.rating,
            bestRating: 10,
            ratingCount: Math.round(drama.views / 10000),
          },
        }
      : null,
  );

  if (!drama) {
    return (
      <div className="flex min-h-screen w-full flex-col bg-background-50">
        <SiteHeader />
        <main className="flex w-full flex-1 items-center justify-center px-4 pt-16">
          <div className="w-full max-w-md rounded-lg border border-background-200 bg-background-100 px-6 py-12 text-center">
            <span className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-file-damage-line text-3xl" />
            </span>
            <h1 className="mt-5 font-heading text-xl font-black text-foreground-950">
              影视资源不存在或已下架
            </h1>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              你访问的作品可能已经调整或链接有误，回到首页看看其他推荐吧。
            </p>
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-home-4-line" />
                返回首页
              </Link>
              <Link
                to="/category"
                className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-800 transition-colors hover:border-primary-300 whitespace-nowrap cursor-pointer"
              >
                浏览分类
              </Link>
            </div>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const related = getRelatedDramas(drama, 4);
  const dramaTopics = topics.filter((topic) => drama.topics.includes(topic.id));

  const metaRows = [
    { label: "类型", value: drama.genres.join(" / ") },
    { label: "年份", value: `${drama.year}` },
    { label: "地区", value: drama.region },
    { label: "片长", value: drama.duration },
    { label: "导演", value: drama.director },
    { label: "主演", value: drama.cast.join(" / ") },
    { label: "更新时间", value: drama.updatedAt },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="w-full flex-1">
        <section className="relative w-full pt-16">
          <div className="relative h-[300px] w-full overflow-hidden md:h-[380px]">
            <img
              src={drama.backdrop}
              alt={`${drama.title} 剧照`}
              title={`${drama.title} ${drama.genres.join(" ")} 剧照`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-foreground-950/50 to-foreground-950/60" />
          </div>

          <div className="mx-auto w-full max-w-6xl px-4 md:px-8">
            <div className="relative -mt-36 flex flex-col gap-5 md:-mt-44 md:flex-row md:gap-7">
              <div className="w-36 shrink-0 sm:w-44 md:w-56">
                <div className="aspect-[3/4.2] w-full overflow-hidden rounded-lg border-2 border-background-50 bg-background-200">
                  <img
                    src={drama.poster}
                    alt={`${drama.title} 海报`}
                    title={`${drama.title} ${drama.genres.join(" ")}`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="min-w-0 flex-1 pb-1 md:pt-24">
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to="/"
                    className="text-[11px] text-foreground-600 transition-colors hover:text-primary-600 cursor-pointer"
                  >
                    首页
                  </Link>
                  <i className="ri-arrow-right-s-line text-[11px] text-foreground-400" />
                  <Link
                    to={`/category?category=${drama.category}`}
                    className="text-[11px] text-foreground-600 transition-colors hover:text-primary-600 cursor-pointer"
                  >
                    {drama.category === "movie" ? "电影" : "电视剧"}
                  </Link>
                  <i className="ri-arrow-right-s-line text-[11px] text-foreground-400" />
                  <span className="text-[11px] text-foreground-900">{drama.title}</span>
                </div>

                <h1 className="mt-2 font-heading text-2xl font-black leading-tight text-foreground-950 md:text-4xl">
                  {drama.title}
                </h1>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <span className="flex items-center gap-1 rounded-md bg-accent-500 px-2 py-1 text-xs font-medium text-background-50">
                    <i className="ri-star-fill text-[10px]" />
                    {drama.rating.toFixed(1)}
                  </span>
                  <span className="rounded-md border border-background-300 bg-background-50 px-2 py-1 text-xs text-foreground-700">
                    {drama.year}
                  </span>
                  <span className="rounded-md border border-background-300 bg-background-50 px-2 py-1 text-xs text-foreground-700">
                    {drama.category === "movie" ? "电影" : "电视剧"}
                  </span>
                  <span className="rounded-md border border-background-300 bg-background-50 px-2 py-1 text-xs text-foreground-700">
                    {drama.duration}
                  </span>
                  <span className="flex items-center gap-1 text-xs text-foreground-600">
                    <i className="ri-fire-line text-accent-600" />
                    {formatViews(drama.views)} 热度
                  </span>
                </div>

                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-700">
                  {drama.summary}
                </p>

                <div className="mt-5 flex flex-wrap items-center gap-2">
                  <Link
                    to={`/category?genre=${encodeURIComponent(drama.genres[0])}`}
                    className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-4 py-2 text-xs font-medium text-background-50 transition-colors hover:bg-primary-700 whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-list-check-2" />
                    查看同类作品
                  </Link>
                  <Link
                    to="/category"
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-xs text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700 whitespace-nowrap cursor-pointer"
                  >
                    返回分类页
                  </Link>
                  <ShareButton title={drama.title} />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-8 md:px-8 md:py-10">
          <div className="mx-auto grid w-full max-w-6xl grid-cols-1 gap-6 lg:grid-cols-[1fr_300px] lg:gap-8">
            <div className="min-w-0">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950 md:text-xl">
                  <span className="h-4 w-1 rounded-full bg-primary-600" />
                  剧情简介
                </h2>
                <p className="mt-3 text-sm leading-7 text-foreground-800 md:text-[15px]">
                  {drama.synopsis}
                </p>
                <p className="mt-4 flex items-start gap-2 rounded-md bg-background-100 px-3 py-2.5 text-[11px] leading-relaxed text-foreground-600">
                  <i className="ri-information-line mt-0.5 text-sm" />
                  本站提供影视内容信息展示，不提供播放与下载服务。以上剧情为演示用虚构简介。
                </p>
              </div>

              <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
                <h2 className="flex items-center gap-2 font-heading text-lg font-black text-foreground-950 md:text-xl">
                  <span className="h-4 w-1 rounded-full bg-accent-500" />
                  作品信息
                </h2>
                <dl className="mt-3 grid grid-cols-1 gap-x-8 gap-y-0 sm:grid-cols-2">
                  {metaRows.map((row) => (
                    <div
                      key={row.label}
                      className="flex items-start gap-3 border-b border-background-200 py-2.5 last:border-b-0"
                    >
                      <dt className="w-16 shrink-0 text-xs text-foreground-500">{row.label}</dt>
                      <dd className="min-w-0 text-xs leading-relaxed text-foreground-900">
                        {row.value}
                      </dd>
                    </div>
                  ))}
                </dl>

                <div className="mt-4 flex flex-wrap items-center gap-2">
                  {drama.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-md bg-secondary-100 px-2 py-1 text-[11px] text-secondary-900"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <aside className="min-w-0">
              <div className="rounded-lg border border-background-200 bg-background-50 p-4">
                <h2 className="text-sm font-bold text-foreground-950">所属分类</h2>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Link
                    to={`/category?category=${drama.category}`}
                    className="rounded-md bg-primary-100 px-2.5 py-1.5 text-xs text-primary-800 transition-colors hover:bg-primary-200 cursor-pointer whitespace-nowrap"
                  >
                    {drama.category === "movie" ? "电影" : "电视剧"}
                  </Link>
                  {drama.genres.map((genre) => (
                    <Link
                      key={genre}
                      to={`/category?genre=${encodeURIComponent(genre)}`}
                      className="rounded-md border border-background-300 px-2.5 py-1.5 text-xs text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                    >
                      {genre}
                    </Link>
                  ))}
                </div>

                {dramaTopics.length > 0 ? (
                  <>
                    <h2 className="mt-5 text-sm font-bold text-foreground-950">收录专题</h2>
                    <div className="mt-3 flex flex-col gap-2">
                      {dramaTopics.map((topic) => (
                        <Link
                          key={topic.id}
                          to={`/category?topic=${topic.id}`}
                          className="flex items-center justify-between rounded-md bg-background-100 px-3 py-2 text-xs text-foreground-800 transition-colors hover:bg-accent-100 cursor-pointer"
                        >
                          <span className="truncate">{topic.name}</span>
                          <i className="ri-arrow-right-s-line" />
                        </Link>
                      ))}
                    </div>
                  </>
                ) : null}

                <h2 className="mt-5 text-sm font-bold text-foreground-950">数据说明</h2>
                <p className="mt-2 text-[11px] leading-relaxed text-foreground-600">
                  评分、热度与更新时间均为站内演示数据，用于展示页面效果，不代表真实排行。
                </p>
              </div>
            </aside>
          </div>
        </section>

        <section className="w-full bg-background-100 px-4 py-10 md:px-8 md:py-12">
          <div className="mx-auto w-full max-w-6xl">
            <div className="mb-5 flex items-end justify-between gap-4">
              <div>
                <h2 className="flex items-center gap-2 font-heading text-xl font-black text-foreground-950 md:text-2xl">
                  <span className="h-5 w-1 rounded-full bg-primary-600" />
                  相关推荐
                </h2>
                <p className="mt-1.5 text-xs text-foreground-600 md:text-sm">
                  根据类型与专题相似度为《{drama.title}》挑选
                </p>
              </div>
              <Link
                to="/category"
                className="flex shrink-0 items-center gap-1 text-xs text-foreground-600 transition-colors hover:text-primary-600 cursor-pointer md:text-sm"
              >
                更多作品
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
            {related.length === 0 ? (
              <p className="rounded-lg border border-dashed border-background-300 py-10 text-center text-sm text-foreground-600">
                暂无相关推荐
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-x-3 gap-y-6 sm:grid-cols-3 md:gap-x-4 lg:grid-cols-4">
                {related.map((item) => (
                  <DramaCard
                    key={item.id}
                    id={item.id}
                    title={item.title}
                    poster={item.poster}
                    year={item.year}
                    category={item.category}
                    genres={item.genres}
                    rating={item.rating}
                    tags={item.tags}
                  />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}